/**
 * @description Composable for managing payment pre-order state with localStorage persistence
 * @author Omar Sanchez Martinez
 * @since 2025-10-17
 */

import { onMounted, ref, watch } from 'vue';
import { useCreatePreOrder } from '../queries/useCreatePreOrder';

export const STRIPE_PRE_ORDER_PAYMENT_GATEWAY_ID_STORAGE_KEY = 'preOrderPaymentGatewayID';

interface PreOrderData {
  preOrderId: string;
  clientSecret: string;
  subscriptionPlanId: number;
  timestamp: number;
}

/**
 * @description Composable that manages pre-order state with localStorage persistence
 * Handles creating, storing, and loading pre-order data
 */
export const usePaymentPreOrder = () => {
  // State
  const preOrderId = ref<string | null>(null);
  const clientSecret = ref<string | null>(null);
  const subscriptionPlanId = ref<number | null>(null);

  // Mutation
  const { mutate: createPreOrderMutation, isPending: isCreating } = useCreatePreOrder();

  /**
   * Load pre-order data from localStorage
   */
  const loadPreOrderFromStorage = (): PreOrderData | null => {
    try {
      const stored = localStorage.getItem(STRIPE_PRE_ORDER_PAYMENT_GATEWAY_ID_STORAGE_KEY);
      if (!stored) return null;

      const data: PreOrderData = JSON.parse(stored);

      // Check if pre-order is still valid (less than 24 hours old)
      const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
      const isExpired = Date.now() - data.timestamp > TWENTY_FOUR_HOURS;

      if (isExpired) {
        clearPreOrder();
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error loading pre-order from storage:', error);
      clearPreOrder();
      return null;
    }
  };

  /**
   * Save pre-order data to localStorage
   */
  const savePreOrderToStorage = (data: Omit<PreOrderData, 'timestamp'>) => {
    try {
      const preOrderData: PreOrderData = {
        ...data,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        STRIPE_PRE_ORDER_PAYMENT_GATEWAY_ID_STORAGE_KEY,
        JSON.stringify(preOrderData),
      );
    } catch (error) {
      console.error('Error saving pre-order to storage:', error);
    }
  };

  /**
   * Create a new pre-order
   * @param planId - ID of the subscription plan
   * @param existingPreOrderId - Optional existing pre-order ID to update
   * @param callbacks - Optional callbacks for success/error
   */
  const createPreOrder = (
    planId: number,
    existingPreOrderId?: string,
    callbacks?: {
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
    },
  ) => {
    createPreOrderMutation(
      {
        subscriptionPlanId: planId,
        preOrderId: existingPreOrderId,
      },
      {
        onSuccess: response => {
          const gatewayResponse = response.data.payment_gateway_response;
          preOrderId.value = gatewayResponse.pre_order_id;
          clientSecret.value = gatewayResponse.client_secret;
          subscriptionPlanId.value = planId;

          // Save to localStorage
          savePreOrderToStorage({
            preOrderId: gatewayResponse.pre_order_id,
            clientSecret: gatewayResponse.client_secret,
            subscriptionPlanId: planId,
          });

          // Call external success callback if provided
          callbacks?.onSuccess?.();
        },
        onError: error => {
          console.error('Error creating pre-order:', error);
          // Error handling is done automatically by the mutation hook

          // Call external error callback if provided
          callbacks?.onError?.(error);
        },
      },
    );
  };

  /**
   * Clear pre-order data from state and localStorage
   */
  const clearPreOrder = () => {
    preOrderId.value = null;
    clientSecret.value = null;
    subscriptionPlanId.value = null;
    localStorage.removeItem(STRIPE_PRE_ORDER_PAYMENT_GATEWAY_ID_STORAGE_KEY);
  };

  /**
   * Restore pre-order data from localStorage
   */
  const restorePreOrder = () => {
    const stored = loadPreOrderFromStorage();
    if (stored) {
      preOrderId.value = stored.preOrderId;
      clientSecret.value = stored.clientSecret;
      subscriptionPlanId.value = stored.subscriptionPlanId;
      return true;
    }
    return false;
  };

  // Watch for changes and sync to localStorage
  watch([preOrderId, clientSecret, subscriptionPlanId], ([orderId, secret, planId]) => {
    if (orderId && secret && planId) {
      savePreOrderToStorage({
        preOrderId: orderId,
        clientSecret: secret,
        subscriptionPlanId: planId,
      });
    }
  });

  // Load from storage on mount
  onMounted(() => {
    restorePreOrder();
  });

  return {
    // State
    preOrderId,
    clientSecret,
    subscriptionPlanId,
    isCreating,

    // Methods
    createPreOrder,
    clearPreOrder,
    loadPreOrderFromStorage,
    restorePreOrder,
  };
};
